import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Check, X } from "lucide-react";
import api, { apiError } from "@/lib/api";
import { StatusBadge } from "@/admin/ui";
import { Button } from "@/components/ui/button";

/* Regulation ↔ Article candidate review queue. Matching is DETERMINISTIC
   (curated aliases, regulators, jurisdictions) — never described as AI.
   Approve = confirmed link (source=editorial, rescans never undo it, Linked
   Coverage updates). Reject = candidate never resurrects. Both are audited. */
export const LinkReviewQueue = () => {
  const qc = useQueryClient();
  const { data } = useQuery({
    queryKey: ["regulation-link-pending"],
    queryFn: async () => (await api.get("/admin/regulation-links/pending")).data,
  });
  const links = data?.links || [];

  const act = async (l: any, status: "linked" | "rejected") => {
    let reason: string | null = null;
    if (status === "rejected") reason = window.prompt("Rejection reason (optional)") || null;
    try {
      await api.patch(`/admin/regulation-links/${l.id}`, { status, ...(reason ? { reason } : {}) });
      toast.success(status === "linked" ? "Link approved — Linked Coverage updated" : "Candidate rejected");
      qc.invalidateQueries({ queryKey: ["regulation-link-pending"] });
    } catch (e) { toast.error(apiError(e)); }
  };

  if (!links.length) return null;
  return (
    <div data-testid="link-review-queue" className="mb-8 rounded-xl border border-[#A78BFA]/25 bg-[#0f0f14] p-4 sm:p-5">
      <div className="flex items-center gap-2.5 mb-1">
        <h2 className="font-heading font-bold text-white text-[15px]">Regulation ↔ Article Review</h2>
        <span data-testid="link-review-count"
          className="rounded-full border border-[#A78BFA]/40 bg-[#A78BFA]/10 px-2 py-0.5 font-mono text-[10px] font-bold text-[#A78BFA]">
          {links.length} pending
        </span>
      </div>
      <p className="text-gray-500 text-xs mb-4">
        Deterministic match candidates awaiting editorial confirmation. Approved links power Linked Coverage on /policy; editorial decisions outrank automation.
      </p>
      <div className="space-y-2.5">
        {links.map((l: any) => (
          <div key={l.id} data-testid={`link-review-row-${l.id}`}
            className="rounded-lg border border-white/[0.08] bg-[#121212] px-3.5 py-3 flex flex-col lg:flex-row lg:items-center gap-3">
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2 flex-wrap">
                <StatusBadge status={l.regulation_status} />
                <span className="text-gray-300 text-[12.5px] font-semibold truncate max-w-[300px]">{l.regulation_title}</span>
                <span className="font-mono text-[9px] uppercase tracking-wide text-gray-600">
                  {(l.regulation_countries || []).join(" · ")}
                </span>
              </div>
              <p className="text-white text-[13px] leading-snug mt-1.5 line-clamp-1">{l.article_title || l.article_slug}</p>
              <div className="flex items-center gap-2.5 mt-1.5 flex-wrap">
                <span className="rounded border border-white/[0.12] bg-white/[0.04] px-1.5 py-0.5 font-mono text-[8.5px] uppercase tracking-wide text-gray-400">
                  {l.relationship_type}
                </span>
                <span className="inline-flex items-center gap-1.5" title="Deterministic match strength">
                  <span className="h-1 w-16 rounded-full bg-white/[0.08] overflow-hidden">
                    <span className="block h-full rounded-full bg-[#00E5FF]" style={{ width: `${Math.round((l.confidence || 0) * 100)}%` }} />
                  </span>
                  <span className="font-mono text-[9px] text-gray-500">{Math.round((l.confidence || 0) * 100)}%</span>
                </span>
                <span className="font-mono text-[9px] text-gray-500 truncate max-w-[420px]">
                  Match: {(l.match_reasons || []).join("; ") || "—"}
                </span>
                {l.article_published_at && (
                  <span className="font-mono text-[9px] text-gray-600">{l.article_published_at.slice(0, 10)}</span>
                )}
              </div>
            </div>
            <div className="flex gap-2 shrink-0">
              <Button size="sm" onClick={() => act(l, "linked")} data-testid={`link-approve-${l.id}`}
                className="bg-[#34D399]/15 border border-[#34D399]/40 text-[#34D399] font-bold hover:bg-[#34D399]/25 h-8">
                <Check className="h-3.5 w-3.5 mr-1" /> Approve
              </Button>
              <Button size="sm" variant="ghost" onClick={() => act(l, "rejected")} data-testid={`link-reject-${l.id}`}
                className="border border-white/[0.12] text-gray-400 hover:text-red-400 hover:border-red-400/40 h-8">
                <X className="h-3.5 w-3.5 mr-1" /> Reject
              </Button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};
